/**
 * EditSectionDialog — per-plan section metadata editor.
 *
 * Edits per-plan overrides for a single section of the Structured Care Plan
 * matrix (title, short display title, summary, portal visibility, category/risk
 * tag). Saves via the per-section meta endpoint. Never touches section rows and
 * never changes the global section catalogue.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';

const NONE = '__none__';
const CATEGORY_OPTIONS = [
  { value: NONE, label: 'None' },
  { value: 'BEHAVIOUR', label: 'Behaviour' },
  { value: 'EOL', label: 'EOL' },
  { value: 'HIGH_RISK', label: 'High Risk' },
  { value: 'CUSTOM', label: 'Custom' },
];

export function EditSectionDialog({ open, onClose, section, catalog, onSave }) {
  const catalogTitle = catalog?.title || '';
  const [titleOverride, setTitleOverride] = useState(section?.title_override ?? '');
  const [shortTitle, setShortTitle] = useState(section?.short_title ?? '');
  const [summary, setSummary] = useState(section?.summary ?? '');
  const [portalVisible, setPortalVisible] = useState(
    section?.portal_visible != null ? !!section.portal_visible : !!catalog?.portal_default,
  );
  const [categoryTag, setCategoryTag] = useState(section?.category_tag || NONE);
  const [categoryLabel, setCategoryLabel] = useState(section?.category_label ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        title_override: titleOverride,
        short_title: shortTitle,
        summary,
        portal_visible: portalVisible,
        category_tag: categoryTag === NONE ? '' : categoryTag,
        category_label: categoryTag === 'CUSTOM' ? categoryLabel : '',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="edit-section-dialog">
        <DialogHeader>
          <DialogTitle>Edit section</DialogTitle>
          <DialogDescription>
            Overrides apply to this care plan only. The global section catalogue and existing rows are not changed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="edit-section-title">Section title</Label>
            <Input
              id="edit-section-title"
              data-testid="edit-section-title"
              value={titleOverride}
              placeholder={catalogTitle}
              onChange={(e) => setTitleOverride(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">Leave blank to use the default: “{catalogTitle}”.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-section-short-title">Short display title</Label>
            <Input
              id="edit-section-short-title"
              data-testid="edit-section-short-title"
              value={shortTitle}
              placeholder="Short label for the left navigation (optional)"
              onChange={(e) => setShortTitle(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-section-summary">Section summary</Label>
            <Textarea
              id="edit-section-summary"
              data-testid="edit-section-summary"
              rows={3}
              value={summary}
              placeholder="Short summary shown on the section header / portal"
              onChange={(e) => setSummary(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="edit-section-portal-toggle" className="block">Portal visibility</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                When off, this whole section is hidden from the participant portal.
              </p>
            </div>
            <Switch
              id="edit-section-portal-toggle"
              data-testid="edit-section-portal-toggle"
              checked={portalVisible}
              onCheckedChange={setPortalVisible}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Category / risk tag</Label>
            <Select value={categoryTag} onValueChange={setCategoryTag}>
              <SelectTrigger data-testid="edit-section-category">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`edit-section-category-opt-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {categoryTag === 'CUSTOM' && (
              <Input
                className="mt-2"
                data-testid="edit-section-category-label"
                value={categoryLabel}
                placeholder="Custom tag label"
                onChange={(e) => setCategoryLabel(e.target.value)}
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="edit-section-cancel" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} data-testid="edit-section-save" disabled={saving}>
            {saving ? 'Saving…' : 'Save section'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditSectionDialog;
